import type { PhotoAppOrderEntity } from './photoAppOrderEntity';
import type { ChildId } from './valueObjects/childId';
import type { CreateDatetime } from './valueObjects/createDatetime';
import type { DeliveryStatus } from './valueObjects/deliveryStatus';
import type { ExpirationDate } from './valueObjects/expirationDate';
import type { LastModifiedDatetime } from './valueObjects/lastModifiedDatetime';
import type { MemberId } from './valueObjects/memberId';
import type { ParentId } from './valueObjects/parentId';
import type { PaymentStatus } from './valueObjects/paymentStatus';
import type { PaymentType } from './valueObjects/paymentType';
import type { Price } from './valueObjects/price';
import type { SalesGroupId } from './valueObjects/salesGroupId';
import type { SalesGroupName } from './valueObjects/salesGroupName';
import type { PhotoOrderJsonPub, PhotoOrderJsonPvt } from '@/types/domain/photo/entities/photoOrderEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';
import { getDateString } from '@/utils/date';

/**
 * 写真販売プロパティ非公開
 */
export type PhotoOrderPropsPvt = {
  /** 販売グループID */
  salesGroupId: SalesGroupId,
  /** 販売グループ名 */
  salesGroupName: SalesGroupName,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 支払ステータス */
  paymentStatus: PaymentStatus,
  /** 販売ステータス */
  deliveryStatus: DeliveryStatus,
  /** 支払タイプ */
  paymentType: PaymentType,
  /** 値段 */
  price: Price,
  /** 期限 */
  expirationDate: ExpirationDate,
  /** 写真リスト */
  photos: PhotoAppOrderEntity[],
  /** 作成日時 */
  createDatetime: CreateDatetime,
  /** 作成メンバーID */
  createMemberId: MemberId,
  /** 最終更新日時 */
  lastModifiedDatetime: LastModifiedDatetime,
  /** 最終更新メンバーID */
  lastModifiedMemberId: MemberId,
}

/**
 * 写真販売プロパティ公開
 */
export type PhotoOrderPropsPub = Record<string, never>

/**
 * 写真販売Entity
 */
export class PhotoOrderEntity extends EntityPub<
  PhotoOrderPropsPvt,
  PhotoOrderJsonPvt,
  PhotoOrderPropsPub,
  PhotoOrderJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    // 生成から１年後に消す
    return this.getFutureYear(this.createDatetime.value, 1).toJSON();
  }

  /**
   * Json取得
   * @returns Json
   */
  toJsonData(): PhotoOrderJsonPvt {
    return {
      salesGroupId: this.salesGroupId.value,
      salesGroupName: this.salesGroupName.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      deliveryStatus: this.deliveryStatus.value,
      paymentStatus: this.paymentStatus.value,
      paymentType: this.paymentType.value,
      price: this.price.value,
      expirationDate: getDateString(this.expirationDate.value),
      photos: this.photos.map((v) => v.toJsonData()),
      createDatetime: this.createDatetime.value.toJSON(),
      createMemberId: this.createMemberId.value,
      lastModifiedDatetime: this.lastModifiedDatetime.value.toJSON(),
      lastModifiedMemberId: this.lastModifiedMemberId.value,
    };
  }

  toJsonPubData(): PhotoOrderJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryPhotoPhotoOrderEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.createDatetime.value.toJSON()}#${this.salesGroupId.value}#${this.parentChildId}`;
  }

  /** サブキー */
  get subKey() {
    return `${this.salesGroupId.value}#${this.parentChildId}#`;
  }

  /**
   * サブキー2
   */
  get subKey2() {
    return `${this.paymentStatus.value}#${this.salesGroupId.value}`;
  }

  /**
   * 注文ID
   */
  get orderId() {
    return this.entityKey;
  }

  /**
   * 親子ID
   */
  get parentChildId() {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /**
   * 販売グループID
   */
  get salesGroupId(): SalesGroupId {
    return this._value.salesGroupId;
  }

  /**
   * 販売グループ名
   */
  get salesGroupName(): SalesGroupName {
    return this._value.salesGroupName;
  }

  /**
   * 支払ステータス
   */
  get paymentStatus(): PaymentStatus {
    return this._value.paymentStatus;
  }

  /**
   * 支払ステータス
   */
  set paymentStatus(paymentStatus: PaymentStatus) {
    this._value.paymentStatus = paymentStatus;
  }

  /**
   * 支払タイプ
   */
  get paymentType(): PaymentType {
    return this._value.paymentType;
  }

  /**
   * 価格
   */
  get price(): Price {
    return this._value.price;
  }

  /**
   * 期限
   */
  get expirationDate(): ExpirationDate {
    return this._value.expirationDate;
  }

  /**
   * 配送ステータス
   */
  get deliveryStatus(): DeliveryStatus {
    return this._value.deliveryStatus;
  }

  /**
   * 配送ステータス
   */
  set deliveryStatus(deliveryStatus: DeliveryStatus) {
    this._value.deliveryStatus = deliveryStatus;
  }

  /**
   * 写真一覧
   */
  get photos(): PhotoAppOrderEntity[] {
    return this._value.photos;
  }

  /**
   * 作成日時
   */
  get createDatetime(): CreateDatetime {
    return this._value.createDatetime;
  }

  /**
   * 作成日時
   */
  set createDatetime(createDatetime: CreateDatetime) {
    this._value.createDatetime = createDatetime;
  }

  /**
   * 作成メンバーID
   */
  get createMemberId(): MemberId {
    return this._value.createMemberId;
  }

  /**
   * 作成メンバーID
   */
  set createMemberId(createMemberId: MemberId) {
    this._value.createMemberId = createMemberId;
  }

  /**
   * 最終更新日時
   */
  get lastModifiedDatetime(): LastModifiedDatetime {
    return this._value.lastModifiedDatetime;
  }

  /**
   * 最終更新日時
   */
  set lastModifiedDatetime(lastModifiedDatetime: LastModifiedDatetime) {
    this._value.lastModifiedDatetime = lastModifiedDatetime;
  }

  /**
   * 最終更新メンバーID
   */
  get lastModifiedMemberId(): MemberId {
    return this._value.lastModifiedMemberId;
  }

  /**
   * 最終更新メンバーID
   */
  set lastModifiedMemberId(lastModifiedMemberId: MemberId) {
    this._value.lastModifiedMemberId = lastModifiedMemberId;
  }
}
